import { UploadOutlined } from "@ant-design/icons"
import { Form, Typography, Upload } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { UploadChangeParam } from "antd/es/upload/interface"
import React, { type ComponentProps } from "react"
import { useTranslation } from "react-i18next"

import { AttachmentsList } from "../../../../../components/AttachmentsList"
import {
  ATTACHMENT_MAX_SIZE_MB,
  validateAttachmentUpload
} from "../../../../../utils/attachment-upload.ts"
import { type ProjectFormData } from "../project-form-data.ts"

const { Text } = Typography

interface AttachmentsSectionProps {
  initialValues?: Partial<ProjectFormData>
  form: FormInstance<ProjectFormData>
}

export function AttachmentsSection({
  initialValues,
  form
}: AttachmentsSectionProps) {
  const { t } = useTranslation()

  const deletedFiles = Form.useWatch("deleted_attachment_ids", form) || []
  const uploadedFiles = Form.useWatch("file_list", form) || []

  const attachments =
    initialValues?.["attachments"]?.filter(
      (file) => !file?.["is_archived"] && !deletedFiles?.includes(file?.id)
    ) || []

  const beforeUpload = (file: File) =>
    validateAttachmentUpload(file) ? false : Upload.LIST_IGNORE

  const handleDeletedFile = (file: { id: string }) => {
    form.setFieldValue("deleted_attachment_ids", [...deletedFiles, file?.id])
  }

  const getValueFromEvent = (e: File[] | UploadChangeParam) => {
    return Array.isArray(e) ? e : e?.fileList
  }

  const uploadProps: ComponentProps<typeof Upload> = {
    multiple: true,
    beforeUpload
  }

  return (
    <>
      <Form.Item
        label={t("projectDocuments.upload.title")}
        name="file_list"
        valuePropName="fileList"
        getValueFromEvent={getValueFromEvent}
      >
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <Text>{t("projectDocuments.upload.dragHint")}</Text>
          <br />
          <Text type="secondary">
            {t("files.upload.maxSize", { size: ATTACHMENT_MAX_SIZE_MB })}
          </Text>
          <br />
          {uploadedFiles.length > 0 && (
            <Text
              type="secondary"
              strong
              style={{ display: "inline-block", marginTop: "0.5rem" }}
            >
              {t("projectDocuments.upload.selected", {
                count: uploadedFiles.length
              })}
            </Text>
          )}
        </Upload.Dragger>
      </Form.Item>

      {!!attachments?.length && (
        <Form.Item>
          <AttachmentsList
            attachments={attachments}
            preview
            onDelete={handleDeletedFile}
          />
        </Form.Item>
      )}
    </>
  )
}
